/**
 * DATA pro stránku „Zdroje KTM" (/admin/zdroje).
 *
 * ⛔ VŠECHNO ZDE JE OVĚŘENÉ MĚŘENÍM nebo opsané z oficiální dokumentace KTM.
 *    Nic není odvozené z chování. U každého tvrzení je uvedeno, odkud pochází.
 *
 * Zdroje pravdy:
 *   - `DataXchange-Documentation.xlsx` (Jakub Kordík, KTM CEE, 13. 1. 2023)
 *   - `219167_short description XML from PIM system.pdf` (tamtéž)
 *   - živý výpis ftp1.ktm.com ze 7. 8. 2026 19:12
 *   - profil souboru 0150_35_Z1_CZK_EN.csv (81 920 řádků) ze 7. 8. 2026
 *   - průchod CBEXPDN_KTM.xml (232 MB, export 1. 8. 2026, verze 3212)
 *
 * Kdo sem něco přidá, MUSÍ uvést, čím to změřil.
 */

export const AUDIT_DATUM = "7. 8. 2026";

/**
 * ROZSAH. Řešíme **KTM a Evropu** — tedy ceník `0150` a `XML_Export_PIM_KTM`.
 * Husqvarna, GASGAS, WP, CFMOTO a MV Agusta na FTP leží, ale nezajímají nás;
 * jsou tu uvedené jen proto, aby bylo jasné, co které číslo v názvu znamená
 * a aby si nikdo omylem nesáhl na špatný soubor. Americké, mexické, japonské
 * a jihoafrické ceníky z dokumentace na našem FTP vůbec nejsou.
 */
export const ROZSAH = "KTM · Evropa · ceník 0150 · PIM XML KTM International";

/**
 * DRUHÉ KOLO AUDITU (Vít: „jakmile to doděláš, tak to znova zaudituj").
 * Všech 24 klíčových tvrzení bylo přeměřeno druhou, nezávislou metodou
 * (regex nad surovým souborem místo XML parseru).
 * Výsledek: 21 sedělo na kus. U 3 se lišilo číslo — a ve všech třech
 * případech byla chybná OVĚŘOVACÍ metoda, ne původní tvrzení:
 * regex nad celým souborem počítal i číselník v ROOT_STAMMDATEN.
 * Rozpad podle kořenů XML to potvrdil: NOT_AVAILABLE je 685× v ROOT_PRODUKTE
 * (z toho 22 s CZ) a 3× v ROOT_STAMMDATEN jako výčet možných hodnot.
 */
export const DRUHE_KOLO = { potvrzeno: 21, opraveno: 0, chybnaMetoda: 3 };

export interface Radek {
  a: string;
  b: string;
  c?: string;
  d?: string;
  zvyraznit?: "cerveny" | "zluty" | "zeleny";
}

/* ------------------------------------------------------------------ */
/* 1. CO POTŘEBUJU → KDE TO JE                                         */
/* ------------------------------------------------------------------ */

export const KDE_TO_JE: Radek[] = [
  {
    a: "Prodejní cena",
    b: "ceník sloupec SalesPrice — MSRP BEZ DPH",
    c: "totéž v article_info.price",
    d: "⛔ PIM ceny NEMÁ (element PREISE je prázdný placeholder) · SPF ceny nemá",
  },
  {
    a: "Nákupní cena",
    b: "ceník PurchasePrice — dealerská BEZ DPH",
    c: "article_info.cost_per_item",
    d: "Ověřeno: ani u jednoho z 81 920 artiklů není nákupní vyšší než prodejní.",
  },
  {
    a: "DPH",
    b: "⚠ V CENÍKU NENÍ. Eshop počítá ×1,21.",
    c: "article_info.vat_rate = 21,00 napevno",
    d: "PIM má prázdný PV_TAX.",
    zvyraznit: "zluty",
  },
  {
    a: "Rabat",
    b: "ceník DiscountGroup — číselník od výrobce",
    c: "vyplněno jen u 23,6 % artiklů, prázdné = standard",
    d: "⛔ Nepočítat z rozdílu cen. ⚠ V datech je 24 hodnot, dokumentace zná jen 20.",
    zvyraznit: "zluty",
  },
  {
    a: "Dostupnost u výrobce",
    b: "ceník StockAvailable (0 / 1 / 2)",
    c: "termín v hqETADate — vyplněn PŘESNĚ když StockAvailable = 2 (2 744 artiklů)",
    d: "⛔⛔ ArticleStatus dostupnost NEŘÍKÁ. To je nejdražší past, co tu byla.",
    zvyraznit: "cerveny",
  },
  {
    a: "Objednatelnost",
    b: "ceník ArticleStatus — 70 JDE OBJEDNAT, jediné NE je 80",
    c: "article_info.article_status",
    d: "Změřeno: u stavu 70 je 5 729 z 6 160 skladem u výrobce (93 %).",
    zvyraznit: "cerveny",
  },
  {
    a: "Sklad Vlašim",
    b: "ABRA CSV → warehouse_stock.qty_on_hand",
    c: "article_info.dealer_stock",
    d: "⛔ V ŽÁDNÉM zdroji od KTM náš sklad není. ⚠ 300 kódů má víc karet → sčítat.",
  },
  {
    a: "Náhrada čísla",
    b: "replacement_articles_0150 + filtr ConversionType = 'Replace'",
    c: "9 521 z 11 184 řádků (7. 8. 2026)",
    d: "⛔⛔ Tentýž soubor nese i rozpady sad. Bez filtru vzniknou nesmysly.",
    zvyraznit: "cerveny",
  },
  {
    a: "Rozpad sady",
    b: "tentýž soubor, ConversionType = 'Set' (1 663 řádků)",
    c: "počet kusů je v NEDOKUMENTOVANÉM sloupci UnitQuantity (u KTM max 10)",
    d: "Bez UnitQuantity nevíš, že sada obsahuje víc kusů téhož dílu — a rozpadne se špatně.",
    zvyraznit: "zluty",
  },
  {
    a: "Fitment náhradního dílu",
    b: "SPF rozkresy → article_compatibility",
    c: "spare_part = pozice ve výkresu",
    d: "⛔ NIKDY z názvu produktu.",
  },
  {
    a: "Fitment PowerParts",
    b: "PIM vazba ZBH2BIKE — 1 992 677 cílů u 5 438 artiklů",
    c: "DealerNet „FITS ON\" (totéž bez filtru trhu)",
    d: "Náhradní díl v ZBH2BIKE většinou není.",
  },
  {
    a: "Hlavní popis",
    b: "PIM TEXTART BESCHRTEXT_GEN_D — ale POUZE na úrovni MODELU (7 375×)",
    c: "fallback dle dokumentace: BESCHRTEXT_ALG (4 973) → BESCHRTEXT_EIGENSCH (3 345)",
    d: "⛔⛔ Na úrovni ARTIKLU popis prakticky NENÍ (BESCHRTEXT_ALG jen 9×, GEN_D 0×). Kdo čte jen ROOT_PRODUKTE, nenajde nic.",
    zvyraznit: "cerveny",
  },
  {
    a: "Marketingový název",
    b: "PIM TEXTART MARKETINGBEZ — 5 636 artiklů",
    c: "na modelu MODELLNAME_GEN (11 722) a MODELLNAME (1 019)",
  },
  {
    a: "Slogan",
    b: "PIM TEXTART SLOGAN — 1 080, jen na úrovni modelu",
    c: "",
    d: "Na artiklu není vůbec.",
  },
  {
    a: "Fotky",
    b: "⭐ SAMOTNÉ SOUBORY jsou uvnitř PIM zipu ve složce PHO/ — nestahují se odjinud",
    c: "XML na ně odkazuje 11 423 unikátními cestami · importér je vytahuje přes bsdtar",
    d: "DealerNet má navíc fotky, které v PIM nejsou — ale základ je v zipu, který stahujeme každý den.",
    zvyraznit: "zeleny",
  },
  {
    a: "Návody a dokumenty",
    b: "⭐ PDF jsou taky uvnitř zipu, ve složce DOK/",
    c: "typ určuje prefix — DOK_PP_BED (návod k PowerParts), DOK_PW_FACTSHEET, DOK_PP_GENECV…",
    d: "⚠ Česky ani jeden — export má jen DE-AT, ES-ES, FR-FR, EN-GB, IT-IT.",
    zvyraznit: "zluty",
  },
  {
    a: "Parametry",
    b: "PIM ATTRIBUTE — číselník 762 atributů v ROOT_STAMMDATEN",
    c: "article_attribute",
    d: "⚠ MODEL_YEAR je KÓD (G, C, D, 9, B…), ne rok — a jeho číselník v exportu NENÍ.",
    zvyraznit: "zluty",
  },
  {
    a: "Kategorie / hierarchie",
    b: "ceník ProductHierarchyL1…L7",
    c: "L1 je vždy „PG&A\" (bez informace), L2–L4 vyplněné ~100 %",
    d: "⛔ L6 NENÍ jen offroad/street — viz sekce Pasti.",
    zvyraznit: "cerveny",
  },
  {
    a: "Velikosti / varianty",
    b: "PIM CONTROLATTRIBUT GROUP_BY na modelu — 2 061 modelů",
    c: "říká, PODLE ČEHO se artikly modelu seskupují (velikost, barva, počet zubů…)",
    d: "Oficiální odpověď výrobce na to, jak stavět varianty.",
  },
  {
    a: "EAN",
    b: "ceník GTIN — 80 418 z 81 920 (98,2 %)",
    c: "article.gtin",
    d: "⛔ ABRA export EAN nemá.",
  },
  {
    a: "Hmotnost",
    b: "⚠ Prakticky nemáme. PIM zná PP_WEIGHT / PW_WEIGHT jen u stovek artiklů.",
    c: "⭐ ArticleWeight je v souboru PGA_Invoice — ten ale NEODEBÍRÁME",
    d: "V PP_WEIGHT jsou i nesmysly (hodnoty −1 a −3).",
    zvyraznit: "zluty",
  },
  {
    a: "Kde se smí prodávat",
    b: "PIM atribut NOT_AVAILABLE — 685 artiklů má omezení, z toho 22 zakazuje ČR",
    c: "",
    d: "⛔ Element LAENDER k tomu NESLOUŽÍ — má vždy stejných 5 zemí (jazyky exportu).",
    zvyraznit: "cerveny",
  },
  {
    a: "Čeština",
    b: "article_translation (náš překlad)",
    c: "",
    d: "⛔⛔ V PIM od KTM ČEŠTINA NENÍ. Export má jen DE-AT, ES-ES, FR-FR, EN-GB, IT-IT.",
    zvyraznit: "cerveny",
  },
  {
    a: "Co je opravdu na eshopu",
    b: "Shopify Admin API",
    c: "article.shopify_* je jen OTISK z posledního sync-backu",
    d: "⚠ Kdo čte shopify_* bez čerstvého sync-backu, počítá se starými daty.",
  },
];

/* ------------------------------------------------------------------ */
/* 2. SOUBORY NA FTP — živý výpis                                      */
/* ------------------------------------------------------------------ */

export const FTP_SOUBORY: Radek[] = [
  { a: "0140_35_Z1_CZK_EN_csv.csv", b: "Husqvarna", c: "13,8 MB", d: "ceník + dostupnost" },
  { a: "0150_35_Z1_CZK_EN_csv.csv", b: "KTM ← tenhle používáme", c: "17,0 MB", d: "81 920 artiklů", zvyraznit: "zeleny" },
  { a: "0910_35_Z1_CZK_EN_csv.csv", b: "WP (vidlice, tlumiče)", c: "1,3 MB", d: "" },
  { a: "1100_35_Z1_CZK_EN_csv.csv", b: "GASGAS", c: "3,6 MB", d: "" },
  { a: "replacement_articles_0140_csv.csv", b: "Husqvarna", c: "7 076 řádků", d: "Replace 6 248 · Set 828" },
  { a: "replacement_articles_0150_csv.csv", b: "KTM ← používáme", c: "11 184 řádků", d: "Replace 9 521 · Set 1 663", zvyraznit: "zeleny" },
  { a: "replacement_articles_0440_csv.csv", b: "⚠ ZNAČKA NEPROKÁZÁNA", c: "1 150 řádků", d: "Prefixy SPC…. Překryv jeho 979 nových čísel s našimi ceníky: KTM 18,5 % · HQV 14,2 % · GASGAS 1,6 % · WP 0 % — žádný ceník tuhle značku nepokrývá. Netvrdit „GasGas\" ani „CFMOTO\", dokud se nezeptáme KTM.", zvyraznit: "zluty" },
  { a: "replacement_articles_0450_csv.csv", b: "MV Agusta", c: "1 306 řádků", d: "prefixy SPM…, MRF…" },
  { a: "replacement_articles_0910_csv.csv", b: "WP", c: "750 řádků", d: "" },
  { a: "replacement_articles_1100_csv.csv", b: "GASGAS", c: "1 565 řádků", d: "prefixy 3GG, A46, A54, BT9" },
  { a: "XML_Export_PIM_KTM_International.zip", b: "KTM — PIM", c: "5,42 GB", d: "rozbalené 232 MB XML" },
  { a: "XML_Export_PIM_HQV_International.zip", b: "Husqvarna — PIM", c: "2,61 GB", d: "nezpracováno" },
  { a: "XML_Export_PIM_WP_International.zip", b: "WP — PIM", c: "1,01 GB", d: "nezpracováno" },
  { a: "XML_Export_PIM_GASGAS_International.zip", b: "GASGAS — PIM", c: "1,02 GB", d: "nezpracováno" },
  { a: "XML_Export_PIM_MVAGUSTA_International.zip", b: "MV Agusta — PIM", c: "15,2 MB", d: "nezpracováno" },
  { a: "XML_Export_PIM_CFMOTO_International.zip", b: "CFMOTO — PIM", c: "15,2 MB", d: "⚠ O téhle značce jsme dosud nevěděli. Velikost je shodná s MV Agustou — stojí za ověření, jestli nejde o tentýž obsah.", zvyraznit: "zluty" },
  { a: "/DEV/ (14 souborů)", b: "vývojová kopie", c: "ceníky 13. 2. 2026 · náhrady 3. 4. 2024 · zipy 25. 9. 2020", d: "⛔⛔ IGNOROVAT. Právě odsud pocházejí zastaralé velikosti PIM zipů (146 MB / 693 MB / 33 MB / 3,7 MB), které kolovaly jako platné.", zvyraznit: "cerveny" },
];

/** Kolik toho na FTP je — aby se nikdo neptal, jestli něco nepřehlédl. */
export const FTP_UPLNOST = {
  koren: 17,
  korenSouboru: 16,
  dev: 14,
  zkousenoVariant: 576,
  nalezenoNavic: 0,
};

/* ------------------------------------------------------------------ */
/* CO JE UVNITŘ PIM ZIPU                                               */
/* ------------------------------------------------------------------ */

export const ZIP_OBSAH: Radek[] = [
  { a: "CBEXPDN_KTM-DN-<verze>-0.xml", b: "vlastní datový soubor", c: "cca 232 MB", d: "verze je v atributu completeversion (dnes 3212)" },
  { a: "PHO/…", b: "FOTKY — jpg a png", c: "cca 10 000 souborů, ~4,3 GB", d: "⭐ Fotky od výrobce máme každý den v zipu. Importér je vytahuje přes bsdtar (zip je Zip64, běžné knihovny ho neotevřou).", zvyraznit: "zeleny" },
  { a: "DOK/…", b: "DOKUMENTY — pdf", c: "cca 1 640 souborů, ~2,8 GB", d: "návody, factsheety, homologace" },
];

/* ------------------------------------------------------------------ */
/* 3. CENÍK — všech 24 sloupců + vyplněnost                            */
/* ------------------------------------------------------------------ */

export const CENIK_SLOUPCE: Radek[] = [
  { a: "PricelistTable", b: "kód přiřazení země", c: "100 %", d: "vždy 68213646-1 — jediná hodnota, bez informace" },
  { a: "ArticleNumber", b: "číslo artiklu (unikát)", c: "100 %", d: "" },
  { a: "ArticleName", b: "název artiklu", c: "100 %", d: "⚠ 4 řádky prázdné. Názvy jsou z velké části NĚMECKY, i když je soubor _EN.", zvyraznit: "zluty" },
  { a: "Currency", b: "měna", c: "100 %", d: "vždy CZK" },
  { a: "SalesPrice", b: "doporučená prodejní cena BEZ DPH", c: "100 %", d: "desetinná ČÁRKA" },
  { a: "PurchasePrice", b: "naše nákupní cena BEZ DPH", c: "100 %", d: "" },
  { a: "DiscountGroup", b: "kód rabatu", c: "23,6 %", d: "prázdné = standard" },
  { a: "MeasureUnit", b: "měrná jednotka", c: "100 %", d: "⚠ pole je jen 2 znaky → kódy jsou ZKRÁCENÉ oproti dokumentaci", zvyraznit: "zluty" },
  { a: "PriceQuantity", b: "množství, pro které cena platí", c: "100 %", d: "✅ Změřeno: VŽDY 1. Cena je za kus, přepočet netřeba.", zvyraznit: "zeleny" },
  { a: "PackageSize", b: "počet kusů v balení", c: "100 %", d: "18 různých hodnot" },
  { a: "ArticleStatus", b: "stav artiklu (aktivní / objednatelný)", c: "99,5 %", d: "⚠ 441 řádků prázdných + hodnota 90, kterou dokumentace nezná", zvyraznit: "zluty" },
  { a: "ProductHierarchy", b: "kód hierarchie (až 14 číslic)", c: "100 %", d: "" },
  { a: "ProductHierarchyL1", b: "úroveň 1", c: "100 %", d: "vždy „PG&A\" — bez informace" },
  { a: "ProductHierarchyL2", b: "úroveň 2", c: "100 %", d: "8 hodnot (Spareparts, …)" },
  { a: "ProductHierarchyL3", b: "úroveň 3", c: "99,7 %", d: "27 hodnot" },
  { a: "ProductHierarchyL4", b: "úroveň 4", c: "99,7 %", d: "66 hodnot" },
  { a: "ProductHierarchyL5", b: "úroveň 5", c: "85,5 %", d: "" },
  { a: "ProductHierarchyL6", b: "úroveň 6", c: "79,6 %", d: "⛔ NENÍ to čistě offroad/street — viz Pasti", zvyraznit: "cerveny" },
  { a: "ProductHierarchyL7", b: "úroveň 7", c: "17,0 %", d: "34 hodnot" },
  { a: "PurposeOfUse", b: "„Placeholder (no data implemented)\"", c: "0 %", d: "✅ Ověřeno: prázdný u všech 81 920. Nepoužívat.", zvyraznit: "zeleny" },
  { a: "PriceValidFrom", b: "datum platnosti NÁKUPNÍ ceny", c: "100 %", d: "formát 2026/01/01" },
  { a: "StockAvailable", b: "dostupnost u výrobce", c: "100 %", d: "0 = není · 1 = skladem · 2 = zná termín" },
  { a: "hqETADate", b: "termín naskladnění u výrobce", c: "3,3 %", d: "✅ Vyplněn přesně u těch 2 744, co mají StockAvailable = 2. Sedí na kus.", zvyraznit: "zeleny" },
  { a: "GTIN", b: "EAN", c: "98,2 %", d: "" },
];

/* ------------------------------------------------------------------ */
/* 4. ČÍSELNÍKY                                                        */
/* ------------------------------------------------------------------ */

export const ARTICLE_STATUS: Radek[] = [
  { a: "20", b: "ano", c: "ANO", d: "standard — 29 188 artiklů" },
  { a: "40", b: "ano", c: "ANO", d: "standard (totéž co 20) — 775" },
  { a: "50", b: "ano", c: "ANO", d: "doprodej, skončí až dojde sklad — 6 067" },
  { a: "60", b: "ano", c: "ANO", d: "doprodej, skončí až dojde sklad — 623" },
  { a: "70", b: "ne", c: "ANO", d: "číslo nahrazeno novým — OBJEDNAT JDE, KTM to překlopí na náhradu (6 160)", zvyraznit: "cerveny" },
  { a: "80", b: "ne", c: "NE", d: "ukončeno — jediný skutečně neobjednatelný stav (38 663)", zvyraznit: "cerveny" },
  { a: "90", b: "?", c: "?", d: "⚠ V DOKUMENTACI NENÍ. V datech 3 artikly, všechny s dostupností 0.", zvyraznit: "zluty" },
  { a: "(prázdné)", b: "?", c: "?", d: "⚠ 441 artiklů bez stavu, všechny s dostupností 0.", zvyraznit: "zluty" },
];

export const STOCK_AVAILABLE: Radek[] = [
  { a: "0", b: "no more available", c: "není", d: "40 563 artiklů" },
  { a: "1", b: "available (on stock)", c: "skladem u výrobce", d: "38 613 artiklů" },
  { a: "2", b: "available at ETA date", c: "není, ale zná termín", d: "2 744 artiklů → datum v hqETADate" },
];

export const MEASURE_UNIT: Radek[] = [
  { a: "ST", b: "kus", c: "81 275", d: "" },
  { a: "GA", b: "trimmings (dok. uvádí GAR)", c: "270", d: "⚠ zkráceno na 2 znaky", zvyraznit: "zluty" },
  { a: "PA", b: "pár (dok. uvádí PAA)", c: "261", d: "⚠ zkráceno", zvyraznit: "zluty" },
  { a: "M", b: "metr", c: "103", d: "" },
  { a: "SE", b: "sada (dok. uvádí SET)", c: "6", d: "⚠ zkráceno", zvyraznit: "zluty" },
  { a: "M2", b: "metr čtvereční", c: "3", d: "" },
  { a: "L", b: "litr", c: "1", d: "" },
  { a: "RO", b: "role (dok. uvádí ROL)", c: "1", d: "⚠ zkráceno", zvyraznit: "zluty" },
  { a: "PAK", b: "balení", c: "0", d: "⛔ V datech se NEVYSKYTUJE, i když ho dokumentace uvádí." },
];

export const DISCOUNT_GROUP: Radek[] = [
  { a: "(prázdné)", b: "standard", c: "62 575", d: "" },
  { a: "A0", b: "50 %", c: "631", d: "" },
  { a: "A1", b: "45 %", c: "52", d: "" },
  { a: "A2", b: "40 %", c: "61", d: "" },
  { a: "A3", b: "35 %", c: "123", d: "" },
  { a: "A4", b: "30 %", c: "1 654", d: "" },
  { a: "A5", b: "25 %", c: "933", d: "" },
  { a: "A6", b: "20 %", c: "1 602", d: "" },
  { a: "A7", b: "15 %", c: "8", d: "" },
  { a: "A8", b: "10 %", c: "8 182", d: "nejčastější nenulový rabat" },
  { a: "A9", b: "0 %", c: "3 199", d: "" },
  { a: "AA", b: "9 %", c: "0", d: "v datech se nevyskytuje" },
  { a: "AB", b: "12 %", c: "0", d: "v datech se nevyskytuje" },
  { a: "AC", b: "5 %", c: "55", d: "" },
  { a: "AU", b: "80 %", c: "51", d: "" },
  { a: "AV", b: "75 %", c: "14", d: "" },
  { a: "AW", b: "70 %", c: "2 015", d: "" },
  { a: "AX", b: "65 %", c: "81", d: "" },
  { a: "AY", b: "60 %", c: "606", d: "" },
  { a: "AZ", b: "55 %", c: "50", d: "" },
  { a: "20 / 35 / 40 / 50 / 60 / 70 / Y5", b: "⚠ NEZNÁMÉ", c: "28 celkem", d: "V oficiálním číselníku NEJSOU. Nevíme, co znamenají — u těchto artiklů rabat nepočítat.", zvyraznit: "cerveny" },
];

/* ------------------------------------------------------------------ */
/* 5. PIM XML                                                          */
/* ------------------------------------------------------------------ */

export const PIM_VAZBY: Radek[] = [
  { a: "ZBH2BIKE", b: "PowerParts, které sedí na motorku", c: "1 992 677 cílů u 5 438 artiklů", d: "hlavní zdroj fitmentu PowerParts", zvyraznit: "zeleny" },
  { a: "EMPFART2ART", b: "doporučené produkty", c: "69 303 cílů u 3 100 artiklů", d: "⚠ máme, ale nepoužíváme — cross-sell děláme ručně", zvyraznit: "zluty" },
  { a: "ET2ARTIKEL", b: "náhradní díly k tomuto artiklu", c: "9 202 cílů u 1 574 artiklů", d: "⚠ nepoužíváme" },
  { a: "MUSSARTIKEL", b: "povinné příslušenství", c: "1 202 cílů u 521 artiklů", d: "⚠ nepoužíváme" },
  { a: "EXCLUDEART", b: "nekombinovat s…", c: "832 cílů u 274 artiklů", d: "⚠ nepoužíváme" },
  { a: "PAKET2BIKE", b: "co je součástí balíčku", c: "0 — u všech 19 523 artiklů je to self-closing <BEZIEHUNGSTYP name=\"PAKET2BIKE\"/>", d: "Dokumentace ho popisuje, data v exportu nejsou. ⚠ Není pravda, že „ho importér ztrácí\" — není co ztrácet.", zvyraznit: "zluty" },
  { a: "MOTOR2BIKE", b: "motor pasující do motorky", c: "0 — prázdné u všech", d: "Dokumentace uvádí „prospective\" (do budoucna).", zvyraznit: "zluty" },
  { a: "SPASPP", b: "?", c: "0 — prázdné u všech", d: "⚠ V dokumentaci NENÍ vůbec.", zvyraznit: "zluty" },
  { a: "ARTIKEL2SET", b: "čísla artiklů sady", c: "—", d: "⛔ Dokumentace ho popisuje, ale v našem exportu se NEVYSKYTUJE. Sady ber z replacement souboru (ConversionType = 'Set').", zvyraznit: "cerveny" },
];

export const PIM_TEXTY: Radek[] = [
  { a: "BEZEICHNUNG", b: "název", c: "artikl 19 523 · model 12 050", d: "vždy" },
  { a: "MODELLNAME_GEN", b: "generický název modelu", c: "model 11 722", d: "" },
  { a: "BESCHRTEXT_GEN_D", b: "HLAVNÍ POPIS (dle dokumentace preferovaný)", c: "model 7 375 · artikl 0", d: "⛔⛔ Na artiklu NENÍ NIKDY. Jen na modelu.", zvyraznit: "cerveny" },
  { a: "BESCHRTEXT_ALG", b: "1. fallback popisu", c: "model 4 973 · artikl 9", d: "" },
  { a: "BESCHRTEXT_EIGENSCH", b: "2. fallback (vlastnosti)", c: "model 3 345 · artikl 3", d: "" },
  { a: "MARKETINGBEZ", b: "marketingový název", c: "artikl 5 636 · model 2", d: "" },
  { a: "SLOGAN", b: "slogan", c: "model 1 080", d: "" },
  { a: "MODELLNAME", b: "název modelu", c: "model 1 019", d: "" },
  { a: "PREISHINWEIS", b: "poznámka k ceně", c: "artikl 1 666", d: "" },
  { a: "BESCHRTEXT_VORTEILE", b: "přednosti", c: "model 8", d: "prakticky prázdné" },
];

export const PIM_JAZYKY: Radek[] = [
  { a: "DE-AT (němčina)", b: "26 016 textů na artiklech · 54 588 na modelech", c: "NEJÚPLNĚJŠÍ", d: "" , zvyraznit: "zeleny" },
  { a: "IT-IT", b: "11 638 · 54 393", c: "", d: "" },
  { a: "ES-ES", b: "11 635 · 54 392", c: "", d: "" },
  { a: "FR-FR", b: "11 634 · 54 393", c: "", d: "" },
  { a: "EN-GB", b: "11 238 · 54 393", c: "nejméně úplná z pětice", d: "" },
  { a: "CS (čeština)", b: "⛔ NENÍ", c: "0", d: "Export je „International\" s pěti kulturami. Čeština v něm není a nebude — všechna čeština u nás je náš vlastní překlad.", zvyraznit: "cerveny" },
];

/* ------------------------------------------------------------------ */
/* 6. PASTI                                                            */
/* ------------------------------------------------------------------ */

export const PASTI: Radek[] = [
  {
    a: "ArticleStatus 70 ≠ „neprodává se\"",
    b: "1 956 produktů bylo prohlášeno za neprodejné podle stavu 70. Vít to vyvrátil na Dealer Shopu („vše skladem!!!\").",
    c: "Číselník od výrobce říká u stavu 70 Orderable = YES. Dostupnost říká StockAvailable.",
    d: "Dokumentaci máme od roku 2023. Číst, ne odvozovat.",
    zvyraznit: "cerveny",
  },
  {
    a: "Náhrady × rozpady sad v jednom souboru",
    b: "Bez filtru vznikaly nesmysly typu „Zadní osa → Matice náboje\" nebo OUTER TUBE za 16 235 Kč „nahrazen\" O-kroužkem za 27 Kč.",
    c: "Filtruj ConversionType = 'Replace'.",
    d: "",
    zvyraznit: "cerveny",
  },
  {
    a: "ProductHierarchyL6 NENÍ jen offroad/street",
    b: "Změřeno na celém ceníku: Offroad 29 997 · Street 20 904 · Offroad/Street 404 — ale také Man 7 366, Unisex 4 476, Kids + Babies 1 202, Woman 834, Women 25, „sub frame\" 14, Kids 12. A 16 686 prázdných.",
    c: "Sloupec míchá segment (díly) s pohlavím (PowerWear). Použitelný na offroad/street je jen u 62,6 % řádků.",
    d: "Kdo z L6 čte segment, musí ostatní hodnoty explicitně vyloučit.",
    zvyraznit: "cerveny",
  },
  {
    a: "Ceník NENÍ v UTF-8",
    b: "Soubor je jednobajtový (latin). Importér ho čte jako UTF-8 → diakritika se rozpadne.",
    c: "1 470 artiklů má poškozený název v databázi. Ověřeno živě: „PAKET GEP?CK\" místo „GEPÄCK\", „?05\" místo „´05\".",
    d: "Z toho 372 u prodejných artiklů (stav ≠ 80).",
    zvyraznit: "cerveny",
  },
  {
    a: "Dokumentace ≠ skutečný soubor",
    b: "replacement_articles má v dokumentaci pořadí SalesOrga, ArticleNumber, … Ve skutečnosti je pořadí jiné a je tam 6. sloupec UnitQuantity, který dokumentace vůbec nezná.",
    c: "MeasureUnit má v datech 2znakové kódy (GA, PA, SE, RO), dokumentace uvádí 3znakové (GAR, PAA, SET, ROL).",
    d: "Čti hlavičku souboru, nespoléhej na pořadí z dokumentace.",
    zvyraznit: "cerveny",
  },
  {
    a: "/DEV na FTP vypadá jako ostrá data",
    b: "Velikosti PIM zipů 146 MB / 693 MB / 33 MB / 3,7 MB, které se tradovaly jako platné, jsou soubory z /DEV z roku 2020.",
    c: "Ostré zipy mají 5,42 GB / 2,61 GB / 1,01 GB / 1,02 GB.",
    d: "Vždy ověřit, ze kterého adresáře číslo pochází.",
    zvyraznit: "cerveny",
  },
  {
    a: "Popisy hledané na špatné úrovni",
    b: "Kdo čte jen ROOT_PRODUKTE (artikly), nenajde hlavní popis — BESCHRTEXT_GEN_D je na artiklu 0×.",
    c: "Popisy jsou na úrovni MODELU: 7 375 hlavních + 4 973 a 3 345 fallbacků.",
    d: "Dokumentace to říká přímo: informace může být na obou úrovních.",
    zvyraznit: "cerveny",
  },
  {
    a: "MODEL_YEAR není rok",
    b: "Hodnoty jsou kódy: G (727×), C (627), D (607), 9 (571), B (505)… Jen část jsou skutečné roky (2000, 2003, 1999).",
    c: "Číselník pro překlad kódů v exportu NENÍ — ROOT_STAMMDATEN ho neobsahuje.",
    d: "Bez číselníku od KTM se z toho rok spolehlivě udělat nedá.",
    zvyraznit: "zluty",
  },
  {
    a: "SPF má tvrdý strop 100 položek",
    b: "Bez stránkování se u výkresů se 100+ pozicemi tiše uloží jen prvních 100.",
    c: "Takhle v databázi chybělo ~20 600 pozic.",
    d: "Vždy stránkovat přes offset.",
    zvyraznit: "cerveny",
  },
  {
    a: "Dostupnost stahujeme 1× denně, KTM ji mění à 15 minut",
    b: "Oficiální rozpis: ceník denně v 5:00, ale dostupnost každých 15 minut.",
    c: "Náš cron běží 1× denně.",
    d: "Kdo chce mít na eshopu pravdu o „skladem u výrobce\", musí stahovat častěji.",
    zvyraznit: "zluty",
  },
];

/* ------------------------------------------------------------------ */
/* ZMĚŘENO A ZAMÍTNUTO                                                 */
/* ------------------------------------------------------------------ */

/**
 * Zdroje a nápady, které vypadaly slibně, ale měřením neobstály.
 * Patří sem, aby je někdo za měsíc nezkoušel znovu.
 */
export const ZAMITNUTO: Radek[] = [
  {
    a: "„CS\" ceník = české názvy od výrobce",
    b: "V archivu je 0150_35_Z1_CZK_CS (19. 9. 2025, 79 524 artiklů). Vypadá to na češtinu od KTM.",
    c: "ZMĚŘENO: proti EN se liší 30 674 názvů (38,6 %) — ale jen VELIKOSTÍ PÍSMEN („BASKET SHAFT\" × „Basket Shaft\"). Českou diakritiku nebo české slovo má 23 z 79 511 názvů (0,0 %).",
    d: "Zamítnuto. Přípona CS neurčuje jazyk názvů. Čeština od KTM neexistuje ani tady.",
  },
  {
    a: "Varianta ceníku _00_ místo _35_",
    b: "V archivu jsou obě. Mohla by být přesnější.",
    c: "ZMĚŘENO na 80 163 společných artiklech: liší se 1 prodejní cena, 4 nákupní, 3 rabaty. Varianta 35 má o 5 artiklů víc.",
    d: "Zamítnuto jako rozdíl. Zůstáváme u _35_, kterou KTM doporučila.",
  },
  {
    a: "PIM jako zdroj cen",
    b: "V XML je element PREISE.",
    c: "ZMĚŘENO: u všech 19 523 artiklů obsahuje tři prázdné placeholdery — PV_BRUTTOPREIS, PV_TAX, PV_FINANZIERUNG_AB. Ani jedna hodnota není vyplněná.",
    d: "Zamítnuto. Ceny výhradně z ceníku.",
  },
  {
    a: "LAENDER jako seznam povolených zemí",
    b: "Dokumentace ho popisuje jako „list of countries in which the article can be showed\".",
    c: "ZMĚŘENO: každý z 19 523 artiklů má tytéž 4 země + GB (AT, ES, FR, GB, IT) = pět jazyků exportu. Nerozlišuje nic.",
    d: "Zamítnuto. Skutečné omezení je v atributu NOT_AVAILABLE.",
  },
  {
    a: "ERP_STATUS z PIM",
    b: "Dokumentace popisuje stavy 05/10/20/50/80/90 a je to jiná škála než ArticleStatus.",
    c: "ZMĚŘENO: atribut je DEKLAROVANÝ ve třídě $KL-ARTICLE_ERP, ale na žádném z 19 523 artiklů není vyplněný. V celém souboru je jediný výskyt — ta deklarace.",
    d: "Nepoužitelné. ⚠⚠ Ale pozor při čtení dokumentace: v PIM by 80 znamenalo AKTIVNÍ, zatímco v ceníku 80 = UKONČENO. Stejné číslo, opačný význam — klasická past „tatáž informace jinde znamená něco jiného\".",
    zvyraznit: "cerveny",
  },
  {
    a: "Země původu a celní kód z PIM",
    b: "Třída $KL-ARTICLE_ERP deklaruje COUNTRY_OF_ORIGIN, CUSTOMS_NO, SUPPLIER, LOT_SIZE, ERP_FORMAT, ID_OLD, ARTNR.",
    c: "ZMĚŘENO: každé z těch polí má v celém souboru jediný výskyt = jen deklaraci. Data KTM neposílá. Jediný naplněný atribut třídy je TYPE (4 108×).",
    d: "Země původu a celní kód se dají získat jen z fakturačního CSV (PGA_Invoice), které neodebíráme.",
  },
  {
    a: "ARTIKEL2SET jako zdroj sad",
    b: "Dokumentace tuhle vazbu popisuje.",
    c: "ZMĚŘENO: v exportu se nevyskytuje. Prázdné jsou i PAKET2BIKE a MOTOR2BIKE (u všech 19 523 artiklů nulový obsah).",
    d: "Sady ber z replacement souboru, ConversionType = 'Set'.",
  },
  {
    a: "Další soubory schované na FTP",
    b: "Podezření, že se něco nelistuje.",
    c: `ZMĚŘENO: NLST i LIST vrací shodně ${17} položek v kořeni a ${14} v /DEV. Přímým dotazem SIZE vyzkoušeno 576 kombinací názvů (značka × 00/35 × Y1/Z1 × CZK/EUR × EN/CS/DE/SK × dvě přípony) — nenašla se ani jedna navíc.`,
    d: "FTP je vyčerpaný. Co tam není, tam opravdu není — včetně PGA_Invoice a amerických souborů z dokumentace.",
  },
];

/* ------------------------------------------------------------------ */
/* 7. CO MÁME A NEPOUŽÍVÁME                                            */
/* ------------------------------------------------------------------ */

export const NEPOUZIVAME: Radek[] = [
  { a: "Doporučené produkty od výrobce", b: "69 303 vazeb u 3 100 artiklů", c: "PIM EMPFART2ART", d: "cross-sell na kartě — dnes ho děláme ručně" },
  { a: "Náhradní díly k artiklu", b: "9 202 vazeb u 1 574 artiklů", c: "PIM ET2ARTIKEL", d: "„náhradní díly k tomuto produktu\"" },
  { a: "Povinné příslušenství", b: "1 202 vazeb u 521 artiklů", c: "PIM MUSSARTIKEL", d: "„k tomuhle potřebujete i…\"" },
  { a: "Nekombinovat", b: "832 vazeb u 274 artiklů", c: "PIM EXCLUDEART", d: "zabránit nesmyslné objednávce" },
  { a: "Seskupení variant od výrobce", b: "2 061 modelů má GROUP_BY", c: "PIM CONTROLATTRIBUT", d: "říká, podle čeho seskupit velikosti/barvy — dnes to řešíme po svém" },
  { a: "Zákaz prodeje po zemích", b: "685 artiklů, z toho 22 zakazuje ČR", c: "PIM NOT_AVAILABLE", d: "⛔ Stojí za kontrolu, jestli těch 22 nemáme na eshopu.", zvyraznit: "cerveny" },
  { a: "Skryté modely", b: "6 modelů má INVISIBLE = J", c: "PIM CONTROLATTRIBUT", d: "výrobce říká „nezobrazovat\"" },
  { a: "Fakturační soubor PGA_Invoice", b: "neodebíráme vůbec", c: "CSV e-mailem, ne FTP", d: "⭐ Obsahuje ArticleWeight (hmotnost vč. obalu), CustomsCode, CountryOfOrigin, VIN, objednané × dodané × backorder. Hmotnosti nám chybí u PPL — tohle je jejich zdroj. Zapíná se u order managementu na HQ.", zvyraznit: "zeleny" },
  { a: "Ceníky HQV / WP / GASGAS / CFMOTO / MV Agusta", b: "5 dalších značek", c: "FTP", d: "leží tam, dnes je neřešíme" },
  { a: "Mediabanka KTM (AssetBank)", b: "media.ktm.com/assetbank-ktm", c: "odkaz je přímo v dokumentaci DataXchange, list PGA_Itemlist", d: "⭐ Nikdy jsme ji nezkoušeli. Základní fotky sice máme ze zipu, ale tohle je oficiální fotobanka výrobce — stojí za pokus dřív, než se něco stahuje z DealerNetu ručně.", zvyraznit: "zeleny" },
];

/* ------------------------------------------------------------------ */
/* OTEVŘENÉ VADY — co je rozbité a ví se o tom                         */
/* ------------------------------------------------------------------ */

export const VADY: Radek[] = [
  {
    a: "Poškozená diakritika v názvech z ceníku",
    b: "Ceník není v UTF-8, importér ho tak ale čte.",
    c: "1 470 artiklů má rozbitý název (z toho 372 u prodejných, tedy stav ≠ 80). Ověřeno živě na katalogovém API: „PAKET GEP?CK TOURING NEU\", „BALL BEARING 6004 NR C3 ?05\".",
    d: "Oprava je jednořádková — číst vstupní stream jako latin1. Nejčastěji se kazí ´ (1 079×), nbsp (432×) a ° (67×).",
    zvyraznit: "cerveny",
  },
  {
    a: "Dostupnost je až o den stará",
    b: "KTM přepisuje dostupnost každých 15 minut, my stahujeme 1× denně.",
    c: "Týká se 81 920 artiklů — hlavně těch 2 744 s termínem a 38 613 „skladem\".",
    d: "Buď stahovat častěji, nebo na eshopu netvrdit „skladem u výrobce\" jako fakt.",
    zvyraznit: "zluty",
  },
  {
    a: "22 artiklů má od výrobce zákaz prodeje v ČR",
    b: "PIM atribut NOT_AVAILABLE obsahuje CZ.",
    c: "Nikdy jsme to nekontrolovali proti eshopu.",
    d: "Projít, jestli některý z nich neprodáváme.",
    zvyraznit: "cerveny",
  },
];

/* ------------------------------------------------------------------ */
/* 8. PŘEHLED ZDROJŮ                                                   */
/* ------------------------------------------------------------------ */

export interface Zdroj {
  id: string;
  nazev: string;
  co: string;
  pristup: string;
  frekvence: string;
  pozor: string;
  stav: "hlavní" | "doplňkový" | "nevyužitý";
}

export const ZDROJE: Zdroj[] = [
  {
    id: "ftp",
    nazev: "KTM FTP DataXchange",
    co: "Ceny, dostupnost, stav artiklu, náhrady, rozpady sad, PIM XML (texty, fotky, parametry, vazby).",
    pristup: "ftp1.ktm.com, TLS explicit. Přihlašovací údaje mimo git.",
    frekvence: "KTM: ceník denně 5:00, dostupnost à 15 min. My: 1× denně.",
    pozor: "Soubory nejsou v UTF-8. Adresář /DEV obsahuje zastaralé kopie.",
    stav: "hlavní",
  },
  {
    id: "spf",
    nazev: "SPF — Spare Parts Finder",
    co: "Rozkresy, pozice dílů ve výkresu, hotspoty (klikací mapa), fitment náhradních dílů, hierarchie modelů.",
    pristup: "GraphQL BFF, hlavička X-Api-Key + Origin. Klíč vázaný na doménu ktm-shop.cz.",
    frekvence: "náš sync 1× týdně (neděle)",
    pozor: "Tvrdý strop 100 položek na dotaz → VŽDY stránkovat. BFF občas restartuje → potřeba backoff.",
    stav: "hlavní",
  },
  {
    id: "katalog",
    nazev: "Katalog na VPS",
    co: "Naše databáze, kam se FTP i SPF slévají. Odsud čte widget na eshopu.",
    pristup: "PostgreSQL na 167.233.165.82, čtecí API /api/catalog/… je bez přihlášení.",
    frekvence: "plní se importy",
    pozor: "⛔ MEZISKLAD, NE PRAMEN. Když něco nesedí, ověřuj proti FTP/SPF, ne proti téhle databázi.",
    stav: "doplňkový",
  },
  {
    id: "dealernet",
    nazev: "DealerNet / KTM Dealer Shop",
    co: "Živý sklad a cena, fotky a fitment, které v PIM ani v ceníku nejsou. U nahrazených čísel překlopí objednávku sám.",
    pristup: "shop.ktmdealer.net, přihlášení Kmoto s.r.o.",
    frekvence: "ručně",
    pozor: "Nemáme API — jen prohlížeč. Blokuje ~1 100 fotek a fitment PowerParts.",
    stav: "doplňkový",
  },
  {
    id: "abra",
    nazev: "ABRA",
    co: "JEDINÝ zdroj skladu ve Vlašimi.",
    pristup: "CSV export přes ktmvlasim.cz",
    frekvence: "sync warehouse",
    pozor: "300 kódů má víc skladových karet → sčítat. EAN v exportu není.",
    stav: "hlavní",
  },
  {
    id: "shopify",
    nazev: "Shopify",
    co: "Co je opravdu venku na eshopu.",
    pristup: "Admin API přes vlastní appku",
    frekvence: "denní otisk",
    pozor: "Sloupce shopify_* v katalogu jsou OTISK — platí jen po čerstvém sync-backu.",
    stav: "hlavní",
  },
  {
    id: "invoice",
    nazev: "PGA_Invoice (fakturační CSV)",
    co: "Hmotnost vč. obalu, celní kód, země původu, VIN, objednané × dodané × backorder, slevy.",
    pristup: "CSV e-mailem — NE přes FTP. Zapíná order management na HQ.",
    frekvence: "podle fakturace",
    pozor: "⭐ NEODEBÍRÁME. Je to jediný zdroj hmotností od výrobce.",
    stav: "nevyužitý",
  },
  {
    id: "mediabank",
    nazev: "KTM MediaBank (AssetBank)",
    co: "Fotobanka výrobce. Odkaz je uvedený přímo v dokumentaci DataXchange.",
    pristup: "media.ktm.com/assetbank-ktm",
    frekvence: "—",
    pozor: "⭐ NIKDY JSME TO NEZKOUŠELI, přitom nám chybí ~1 100 fotek.",
    stav: "nevyužitý",
  },
];
